<template>
	<div class="subscription-column" :class="`subscription-column--${carttype}`">
		<div class="subscription-column-header">
			<div class="subscription-column-header-top">
				<h2 class="subscription-column-heading">
					{{ column_heading }}
					<span class="subscription-column-count">{{ count }}</span>
				</h2>
				<button
					v-if="carttype == 'active' && switch_target_count"
					type="button"
					class="subscription-column-switch"
					:aria-pressed="one_off_view"
					@click="one_off_view = !one_off_view"
				>
					{{ switch_label }}
				</button>
				<a
					v-if="carttype == 'shopping-cart' && count"
					href="/products"
					class="subscription-column-continue"
				>
					{{ __("Continue shopping") }}
					<svg class="icon icon-md" aria-hidden="true"><use href="#icon-arrow-right"></use></svg>
				</a>
			</div>
			<p v-if="subtitle" class="subscription-column-subtitle">{{ subtitle }}</p>
		</div>

		<div v-if="count" class="subscription-column-body">
			<div class="subscription-column-cards">
				<template v-for="group in subscription_groups" :key="group.key">
					<cart-categories :category="group.family" :start_date="group.start_date"></cart-categories>
					<subscription-card
						v-for="item in group.items"
						:key="item.name"
						:item="item"
						:users="users"
						:readonly="readonly"
						:evaluation_note="evaluation_column"
						:variable_price_marker="carttype == 'in-progress'"
						@minus-qty="(i) => minusQty(i)"
						@plus-qty="(i) => plusQty(i)"
						@remove-item="(i) => removeItem(i)"
					/>
				</template>
				<guest-cart-line
					v-for="item in guest_unpriced"
					:key="item.product_id"
					:product_id="item.product_id"
					:qty="item.qty"
					@remove="remove_guest_line(item.product_id)"
				/>
				<!-- Guest only: stored lines the catalogue no longer returns. Kept with the cart
				     lines, before the totals and the order step, so they read as part of the
				     cart. Not counted in the badge or the totals. -->
				<guest-cart-line
					v-for="item in guest_unavailable"
					:key="item.product_id"
					:product_id="item.product_id"
					:qty="item.qty"
					:note="__('This product is no longer available.')"
					@remove="remove_guest_line(item.product_id)"
				/>
			</div>

			<template v-if="carttype == 'shopping-cart'">
				<!-- Totals over a partly priced guest cart would be wrong: the notice replaces them. -->
				<p v-if="guest_failed" class="subscription-column-vat-note">{{ __("Some prices could not be loaded. Refresh the page to try again.") }}</p>
				<cart-total v-else :subscription_object="subscription" carttype="shopping-cart"></cart-total>
				<cart-validation :subscription_object="subscription" @validate_cart="(vat_number, trial, options) => validate_cart(vat_number, trial, options)" @remove_non_eval_items="(line_ids) => removeNonEvalItems(line_ids)" @get_subscription="emit('get_subscription')"></cart-validation>
			</template>
			<div v-else-if="carttype == 'in-progress'" class="subscription-column-totals">
				<div class="total-row">
					<h6>{{ __("Total Setup and Purchase") }}<sup class="total-note-marker">*</sup></h6>
					<h6>{{ ioi_format_currency(one_time_total, subscription.currency) }}</h6>
				</div>
				<div class="total-row">
					<h6>{{ __("Total Subscription") }}<sup class="total-note-marker">*</sup></h6>
					<h6>{{ ioi_format_currency(recurring_total, subscription.currency) }}</h6>
				</div>
				<div class="total-row">
					<h6>{{ __("Setup time") }}<sup class="total-note-marker">**</sup></h6>
					<h6>{{ setup_time_total }} {{ __("Hours") }}</h6>
				</div>
				<p class="subscription-column-vat-note">{{ __("Prices exclude VAT") }}<sup class="total-note-marker">*</sup></p>
				<p v-if="has_evaluation" class="subscription-column-vat-note subscription-column-evaluation">{{ __("Subscription for evaluation, not an effective order") }}</p>
				<setup-time-footnote :subscription_items="subscription.subscription_items" />
			</div>
			<!-- No footer in the one-off view: subtotal is recurring-only, so
			     "Total Subscription" over a list of one-off lines would be wrong twice
			     over. one_off_view is only ever set by the Active column's switch,
			     so this reads as the plain v-else it replaces everywhere else. -->
			<template v-else-if="!one_off_view">
				<div class="subscription-column-subtotal">
					<h5>{{ subtotal_label }}</h5>
					<h5>{{ ioi_format_currency(subtotal, subscription.currency) }}</h5>
				</div>
				<!-- Under the subtotal's two-child row, never in it. -->
				<p v-if="has_evaluation" class="subscription-column-vat-note subscription-column-evaluation">{{ __("Subscription for evaluation, not an effective order") }}</p>
			</template>
		</div>

		<div v-else class="subscription-column-empty">
			<h5>{{ empty_heading }}</h5>
			<p>{{ empty_body }}</p>
			<a v-if="carttype == 'shopping-cart'" href="/products" class="btn btn-primary">{{ __("Browse products") }}</a>
		</div>

		<!-- Guest only, and only when nothing else is in the cart: the lines the catalogue no
		     longer returns, under the empty state, so the visitor can still remove them.
		     With other lines present they sit among the cards above instead. -->
		<div v-if="guest_unavailable.length && !count" class="subscription-column-body">
			<div class="subscription-column-cards">
				<guest-cart-line
					v-for="item in guest_unavailable"
					:key="item.product_id"
					:product_id="item.product_id"
					:qty="item.qty"
					:note="__('This product is no longer available.')"
					@remove="remove_guest_line(item.product_id)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ioi_format_currency } from "./format_currency";
import CartCategories from "./CartCategories.vue";
import CartTotal from "./CartTotal.vue";
import CartValidation from "./CartValidation.vue";
import SetupTimeFootnote from "./SetupTimeFootnote.vue";
import SubscriptionCard from "./SubscriptionCard.vue";
import GuestCartLine from "./GuestCartLine.vue";
import { computed, ref, watch } from 'vue'

const props = defineProps({
	subscription: {
		type: Object,
		default: () => { return {} }
	},
	// "shopping-cart" | "active" | "in-progress" - drives read-only enforcement,
	// footer type, and the empty/populated copy below.
	carttype: {
		type: String,
		default: "active"
	},
	// Column heading text (already resolved/translated by the caller, matching
	// CartPage's existing "your"/reseller-view-as wording pattern).
	heading: {
		type: String,
		default: ""
	},
	// Computed once in CartPage over the full, unfiltered items array - never
	// recomputed per-column, so best_max_user_not_achieved stays correct across
	// the split (see the plan's number_of_users note).
	users: {
		type: Number,
		default: 0
	}
})

const emit = defineEmits(['get_subscription'])

const readonly = computed(() => props.carttype != 'shopping-cart')

// The mentions of the lines ordered for evaluation (trial_requested), on their cards and under the totals:
// in the In progress and Active columns only, never in the Cart's.
const evaluation_column = computed(() => props.carttype == 'in-progress' || props.carttype == 'active')

// Guest cart only (CartPage.apply_guest_cart): the stored lines a failed lookup could not
// price, the lines the catalogue no longer returns, and whether the lookup failed. Empty
// and false for a server cart, which carries no guest key.
const guest = computed(() => props.subscription.guest || {})
const guest_unpriced = computed(() => guest.value.unpriced || [])
const guest_unavailable = computed(() => guest.value.unavailable || [])
const guest_failed = computed(() => !!guest.value.failed)

// LEFT column only: which of its two views is showing. false = the recurring view
// it has always shown, true = the one-off view. Local by design - the parent hands
// this column every Active line already, so the second view is a subset of data
// already in hand: no extra request, no fourth bucket in CartPage, nothing for the
// parent to know about.
//
// Survives a data refresh: the three <subscription-column> elements carry no :key
// and sit at fixed positions, so Vue patches this instance rather than re-creating
// it and setup() never re-runs.
const one_off_view = ref(false)

// The single source of "what this column is showing". Identity for the other two
// columns - same reference, undefined included - so their render path is untouched.
const visible_items = computed(() => {
	const items = props.subscription.subscription_items
	if (props.carttype != 'active' || !items) {
		return items
	}
	// Truthiness, not === false: a line whose is_recurring key were ever absent
	// (with_product_names returns early when no line carries a product_id) still
	// lands in exactly one view instead of falling through both and vanishing.
	return items.filter(item => one_off_view.value ? !item.is_recurring : item.is_recurring)
})

// The totals' mention: at least one of the lines this column shows is an evaluation. Over visible_items, so
// in the Active column it follows the recurring view, whose lines its subtotal sums; the one-off view has
// no subtotal to put it under.
const has_evaluation = computed(() => {
	return evaluation_column.value && (visible_items.value || []).some(item => item.trial_requested)
})

// Same family can carry different start_date values (set per-row) - group by
// both so each distinct date gets its own header instead of a shared one.
const subscription_groups = computed(() => {
	if (!visible_items.value) {
		return []
	}

	const groups = {}
	for (const item of visible_items.value) {
		const family = item.product_family ?? 'null'
		const start_date = item.start_date || ''
		const key = `${family}::${start_date}`
		if (!groups[key]) {
			groups[key] = { key, family, start_date, items: [] }
		}
		groups[key].items.push(item)
	}

	return Object.values(groups).sort((a, b) => {
		if (a.family !== b.family) {
			return a.family < b.family ? -1 : 1
		}
		return a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0
	})
})

// Drives three things at once, which is why repointing it at visible_items is the
// whole of the view switch's plumbing: the header count badge (:7), the
// body-vs-empty-state gate (:21/:63), and the subtitle's truthiness check below.
//
// A guest cart's unpriced lines count too: they are in the cart, only their price is
// missing, so they must not turn the column into its empty state. Always 0 for a server
// cart.
const count = computed(() => (visible_items.value || []).length + guest_unpriced.value.length)

// The DESTINATION view's line count. The switch hides itself when pressing it would
// land on an empty column - same gate .subscription-column-continue applies with
// `&& count`.
const switch_target_count = computed(() => {
	return (props.subscription.subscription_items || []).filter(
		item => one_off_view.value ? item.is_recurring : !item.is_recurring
	).length
})

// Always names where the button GOES, never where you are.
const switch_label = computed(() => {
	return one_off_view.value ? __("Your active subscription") : __("One-off purchases")
})

// The <h2> renders the `heading` prop, which the parent resolves and translates
// (see the prop's contract above). The second view is this component's own concept,
// so it overrides the text locally rather than pushing a view flag up to CartPage.
const column_heading = computed(() => {
	return props.carttype == 'active' && one_off_view.value ? __("One-off purchases") : props.heading
})

// Fall back rather than strand the user on an empty one-off view - removing the
// last one-off line should return the column to what it normally shows. Watching
// `count` works because in the one-off view count IS the one-off count; no loop,
// since the guard is false on the re-entry that flipping the ref triggers.
watch(count, (visible_count) => {
	if (one_off_view.value && !visible_count) {
		one_off_view.value = false
	}
})

// LEFT column subtotal. Cannot be sourced from the backend's total_active
// aggregate - that sums across every row regardless of Active/Inactive/Under Process
// status, see the plan's bucket-split notes. Computed client-side from the same
// per-item fields SubscriptionCard already reads (active_qty vs qty_ordered).
//
// Recurring lines only, per the is_recurring key with_product_names() resolves from
// the Pricing Unit. Both the label ("Total Subscription") and the subtitle ("Running now")
// read as a rate, and a one-time line was ordered once, at order time - it does not
// recur, so adding it inflates the figure by an amount the customer is never
// charged again.
const subtotal = computed(() => {
	if (!props.subscription.subscription_items) {
		return 0.0
	}
	return props.subscription.subscription_items.reduce((sum, item) => {
		if (!item.is_recurring) {
			return sum
		}
		const qty = item.status == "Under Process" ? item.qty_ordered : item.active_qty
		return sum + (qty || 0) * (item.enduser_price || 0)
	}, 0.0)
})

// MIDDLE column only. Same per-item fields and the same qty rule as subtotal
// above, but bucketed by the is_recurring key with_product_names() resolves from
// the line's Pricing Unit - never from unit_of_measure.
//
// install_cost is added once per line and is NOT multiplied by qty, and it lands
// in the one-time bucket whether or not the line itself is recurring. Both points
// mirror calculate_totals(), which is what the Cart column already shows, so a
// line reads the same on both sides of an order.
function line_qty(item) {
	return item.status == "Under Process" ? item.qty_ordered : item.active_qty
}

const one_time_total = computed(() => {
	if (!props.subscription.subscription_items) {
		return 0.0
	}
	return props.subscription.subscription_items.reduce((sum, item) => {
		const line = item.is_recurring ? 0.0 : (line_qty(item) || 0) * (item.enduser_price || 0)
		return sum + line + (item.install_cost || 0)
	}, 0.0)
})

const recurring_total = computed(() => {
	if (!props.subscription.subscription_items) {
		return 0.0
	}
	return props.subscription.subscription_items.reduce((sum, item) => {
		return sum + (item.is_recurring ? (line_qty(item) || 0) * (item.enduser_price || 0) : 0.0)
	}, 0.0)
})

const setup_time_total = computed(() => {
	if (!props.subscription.subscription_items) {
		return 0.0
	}
	return props.subscription.subscription_items.reduce((sum, item) => {
		return sum + (item.estimated_setup_time || 0)
	}, 0.0)
})

const subtotal_label = computed(() => {
	return props.carttype == 'active' ? __("Total Subscription") : __("Will add")
})

// Empty-case text intentionally omitted for "active"/"in-progress" - the dedicated
// empty-state block below (empty_heading/empty_body) already says this, with more
// useful detail (why it's empty, what happens next). A subtitle repeating the same
// idea one line above it was redundant, not reinforcing.
const subtitle = computed(() => {
	if (props.carttype == 'shopping-cart') {
		return count.value ? __("Not ordered yet · adjust quantities below") : __("Empty — nothing selected")
	}
	if (props.carttype == 'active') {
		// Nothing in the one-off view: the subtitle quotes subtotal, which counts
		// recurring lines only. v-if="subtitle" then drops the <p> and its top
		// margin with it, so the header closes up cleanly.
		if (one_off_view.value) {
			return ""
		}
		return count.value
			? __("Running now · {0}", [ioi_format_currency(subtotal.value, props.subscription.currency)])
			: ""
	}
	// in-progress
	return count.value ? __("Orders not yet active") : ""
})

// The one-off arms are a backstop, not a normal state: the watch above returns the
// column to the recurring view as soon as the last one-off line goes, and the switch
// hides itself when the destination is empty. They cover the opening render of a
// subscription that has neither kind of line.
const empty_heading = computed(() => {
	if (props.carttype == 'shopping-cart') return __("Your cart is empty")
	if (props.carttype == 'active') return one_off_view.value ? __("No one-off purchases") : __("No active subscriptions")
	return __("Nothing pending")
})

const empty_body = computed(() => {
	if (props.carttype == 'shopping-cart') return __("Pick a starter pack in the catalogue, then add the users and modules you need.")
	if (props.carttype == 'active') {
		return one_off_view.value
			? __("Products you buy outright appear here.")
			: __("Once an order is set up, every purchase appears here as its own entry.")
	}
	return __("Your orders wait here while your VAR Expert schedules the setup.")
})

// Mutating calls only ever fire from the interactive (shopping-cart) column - readonly
// columns never render the controls that would emit minus-qty/plus-qty/remove-item
// (see SubscriptionCard), so these are unreachable there rather than merely unused.
//
// The unused `r` parameter below is deliberate, not an oversight: frappe.call()
// (request.js) returns the jqXHR from $.ajax(...).done(...).fail(...) - .then(onFulfilled)
// only ever runs on the .done() path (HTTP success), so the response never needs
// inspecting to guard against a failed call.
function update_subscription(line_id, qty) {
	// A guest line's id is its product_id (guest_cart.to_line). The change goes to the local
	// guest cart, whose change event makes CartPage rebuild the column.
	if (ioi_partners.is_guest()) {
		ioi_partners.guest_cart.set_qty(line_id, qty)
		return
	}
	frappe.call({
		method: "ioi_partners.www.subscription.update_subscription",
		args: {
			line_id: line_id,
			qty: qty,
			// Whose cart this page shows (public/js/cart/focus.js).
			expected_customer: ioi_partners.expected_customer(),
		}
	}).then(r => {
		emit('get_subscription')
	})
}

// The order (www/subscription.validate_cart), with the customer's choice on the VAT number's check (vat_check,
// CartValidation.vue), sent only when there is one. Refused, its messages are shown as before (frappe.call), and
// on_refused says what the page does next.
function validate_cart(vat_number, trial, { vat_check = null, on_refused = null } = {}) {
	frappe.call({
		method: "ioi_partners.www.subscription.validate_cart",
		args: {
			vat_number: vat_number,
			trial: trial,
			expected_customer: ioi_partners.expected_customer(),
			...(vat_check ? { vat_check: vat_check } : {}),
		}
	}).then(r => {
		emit('get_subscription')
		window.location = `/confirmation?trial=${trial ? 1 : 0}&var=${props.subscription.var_id}`
	}, (xhr) => {
		if (on_refused) {
			on_refused(xhr)
		}
	})
}

function removeNonEvalItems(line_ids) {
	frappe.call({
		method: "ioi_partners.www.subscription.remove_products_from_subscription",
		args: {
			line_ids: line_ids,
			expected_customer: ioi_partners.expected_customer(),
		}
	}).then(r => {
		emit('get_subscription')
	})
}

function removeItem(item) {
	update_subscription(item.name, 0.0)
}

// Guest rows with no card behind them (GuestCartLine): unpriced or no longer available.
function remove_guest_line(product_id) {
	ioi_partners.guest_cart.set_qty(product_id, 0)
}

function plusQty(item) {
	update_subscription(item.name, item.qty_in_basket + 1)
}

function minusQty(item) {
	update_subscription(item.name, item.qty_in_basket - 1)
}
</script>